import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileText, Upload, Eye, EyeOff, Trash2, Download, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EmployeePolicies() {
  const [policies, setPolicies] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const isAdmin = user.role === 'admin';

  const fetchPolicies = async () => {
    try {
      const res = await axios.get(`${API}/hrms/policies`, { headers: getHeaders() });
      setPolicies(res.data);
    } catch { toast.error('Failed to fetch policies'); }
  };

  useEffect(() => { fetchPolicies(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please select a file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title || file.name);
      formData.append('description', description);
      await axios.post(`${API}/hrms/policies?title=${encodeURIComponent(title || file.name)}&description=${encodeURIComponent(description)}`, formData, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Policy uploaded');
      setUploadOpen(false); setTitle(''); setDescription(''); setFile(null);
      fetchPolicies();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const handleToggle = async (policy) => {
    try {
      const res = await axios.put(`${API}/hrms/policies/${policy.id}/toggle`, {}, { headers: getHeaders() });
      toast.success(res.data.visible ? 'Policy published' : 'Policy hidden');
      fetchPolicies();
    } catch { toast.error('Failed to toggle visibility'); }
  };

  const handleDelete = async (policy) => {
    if (!window.confirm(`Delete policy "${policy.title}"?`)) return;
    try {
      await axios.delete(`${API}/hrms/policies/${policy.id}`, { headers: getHeaders() });
      toast.success('Policy deleted'); fetchPolicies();
    } catch { toast.error('Failed to delete policy'); }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFileIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    const colors = { pdf: 'text-red-600', doc: 'text-blue-600', docx: 'text-blue-600', pptx: 'text-orange-600', ppt: 'text-orange-600', xlsx: 'text-emerald-600', xls: 'text-emerald-600' };
    return colors[ext] || 'text-slate-600';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="policies-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Employee Policies</h1>
          <p className="text-slate-600">{isAdmin ? 'Upload and manage company policies' : 'View company policies and documents'}</p>
        </div>
        {isAdmin && (
          <Button data-testid="upload-policy-btn" onClick={() => setUploadOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
            <Upload size={16} className="mr-2" />Upload Policy
          </Button>
        )}
      </div>

      {policies.length === 0 ? (
        <div className="bg-white border border-blue-100 rounded p-12 text-center">
          <Shield size={48} className="mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500" data-testid="no-policies-msg">No policies available{isAdmin ? '. Upload your first policy above.' : '.'}</p>
        </div>
      ) : (
        <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-blue-50 border-b border-blue-100">
                <TableHead className="font-bold text-blue-900">Policy</TableHead>
                <TableHead className="font-bold text-blue-900">Description</TableHead>
                <TableHead className="font-bold text-blue-900">File</TableHead>
                <TableHead className="font-bold text-blue-900">Size</TableHead>
                {isAdmin && <TableHead className="font-bold text-blue-900">Visibility</TableHead>}
                <TableHead className="font-bold text-blue-900">Uploaded</TableHead>
                <TableHead className="font-bold text-blue-900">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {policies.map(p => (
                <TableRow key={p.id} data-testid={`policy-row-${p.id}`} className="hover:bg-blue-50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText size={18} className={getFileIcon(p.file_name)} />
                      <span className="font-semibold text-blue-900">{p.title}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600 max-w-[250px] truncate">{p.description || '-'}</TableCell>
                  <TableCell className="text-xs text-slate-500">{p.file_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatSize(p.file_size || 0)}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <button data-testid={`toggle-policy-${p.id}`} onClick={() => handleToggle(p)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-bold transition-colors ${p.visible ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                        {p.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                        {p.visible ? 'Visible' : 'Hidden'}
                      </button>
                    </TableCell>
                  )}
                  <TableCell className="text-xs text-slate-500">{p.uploaded_by_name}<br />{p.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <a href={`${BACKEND_URL}${p.file_url}`} target="_blank" rel="noreferrer" data-testid={`download-policy-${p.id}`}>
                        <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-900 hover:bg-blue-50" title="Download"><Download size={14} /></Button>
                      </a>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" data-testid={`delete-policy-${p.id}`} onClick={() => handleDelete(p)} className="text-red-600 hover:text-red-800 hover:bg-red-50" title="Delete"><Trash2 size={14} /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900">Upload Policy</DialogTitle>
            <DialogDescription>Upload a policy document. You can control visibility after upload.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Title</Label>
              <Input data-testid="policy-title-input" value={title} onChange={e => setTitle(e.target.value)} className="mt-2 rounded border-blue-200" placeholder="e.g., Leave Policy 2026" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Description</Label>
              <textarea data-testid="policy-desc-input" value={description} onChange={e => setDescription(e.target.value)} className="mt-2 flex w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm min-h-[60px]" placeholder="Brief description..." />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">File (PDF, DOCX, PPTX, etc.)</Label>
              <Input data-testid="policy-file-input" type="file" onChange={e => setFile(e.target.files[0])} className="mt-2 rounded border-blue-200" accept=".pdf,.doc,.docx,.pptx,.xlsx,.txt,.csv" />
            </div>
            <Button type="submit" data-testid="submit-policy-btn" disabled={uploading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">
              {uploading ? 'Uploading...' : 'Upload Policy'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
